import React, {useState} from 'react';

import sessionStore from 'client/Session/SessionStore';
import {useWindowSize} from 'lib/hooks/useWindowSize';
import {local} from 'lib/StorageUtil';

import {GetStartedModule} from './Home/GetStartedModule';
import {Home} from './Home/Home';
import {TeacherDashboardLayout} from './Layout/TeacherDashboardLayout';

interface Props {
  location?: {
    pathname: string;
  };
}

// Below this width the rail can't sit beside the content, so it becomes an overlay (matches Tailwind `m:`).
const RAIL_SIDE_BY_SIDE_MIN_WIDTH = 1020;

/**
 * Teacher Home owns the Get Started rail. On wide screens it's a persistent side panel whose collapsed
 * state is remembered (localStorage). On narrow screens (< m) it's an overlay that defaults closed — so
 * it never covers the content on load and re-closes whenever we drop to narrow
 */
export const TeacherDashboard = ({location}: Props) => {
  const pathname = location?.pathname ?? '/home';
  const storageKey = `${sessionStore.getUserId()}-homeHideGetStarted`;
  const [collapsed, setCollapsed] = useState<boolean>(() => Boolean(local.get(storageKey)));
  const [narrowRailOpen, setNarrowRailOpen] = useState(false);

  // useWindowSize starts at 0 and fills in after mount; fall back to the live width so the very
  // first render already reflects the real breakpoint.
  const [windowWidth] = useWindowSize();
  const effectiveWidth = windowWidth || (typeof window === 'undefined' ? 0 : window.innerWidth);
  const isNarrow = effectiveWidth < RAIL_SIDE_BY_SIDE_MIN_WIDTH;

  const updateCollapsed = (next: boolean) => {
    local.set(storageKey, next);
    setCollapsed(next);
  };

  // `collapsed` is the saved open/closed preference; `narrowRailOpen` only keeps the overlay from
  // auto-covering content on a fresh narrow load. Both sync on every toggle, so an intentionally closed
  // rail stays closed (and an open one stays open) when the viewport crosses the breakpoint.
  const railOpen = isNarrow ? narrowRailOpen : !collapsed;
  const openRail = () => {
    updateCollapsed(false);
    setNarrowRailOpen(true);
  };
  const closeRail = () => {
    updateCollapsed(true);
    setNarrowRailOpen(false);
  };

  return (
    <TeacherDashboardLayout
      pathname={pathname}
      rightRail={<GetStartedModule onDismiss={closeRail} open={railOpen} overlay={isNarrow} />}
    >
      <Home getStartedCollapsed={!railOpen} onReopenGetStarted={openRail} />
    </TeacherDashboardLayout>
  );
};
